import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAdmin } from '@/hooks/useAdmin';
import { FileEdit, RefreshCw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StaticPagesList() {
  const { getAccessTokenSilently } = useAdmin();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retranslating, setRetranslating] = useState(null);

  const load = useCallback(async () => {
    const token = await getAccessTokenSilently();
    const r = await fetch(`${API}/admin/static-pages`, { headers: { Authorization: `Bearer ${token}` } });
    if (r.ok) setPages((await r.json()).pages || []);
    setLoading(false);
  }, [getAccessTokenSilently]);

  useEffect(() => { load(); }, [load]);

  async function retranslate(slug) {
    setRetranslating(slug);
    try {
      const token = await getAccessTokenSilently();
      const r = await fetch(`${API}/admin/static-pages/${slug}/retranslate`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }
      });
      if (r.ok) toast.success(`Prevedeno: ${slug}`);
      else toast.error('Greška');
    } finally { setRetranslating(null); }
  }

  async function runImport() {
    if (!window.confirm('Ponovno importirati sve static stranice sa e-mina.eu?')) return;
    const token = await getAccessTokenSilently();
    toast.info('Import pokrenut...');
    const r = await fetch(`${API}/admin/static-pages/import-all`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}` }
    });
    if (r.ok) { const d = await r.json(); toast.success(`Importirano ${(d.results || []).filter(x => x.status === 'ok').length} stranica`); load(); }
    else toast.error('Greška pri importu');
  }

  if (loading) return <div className="p-10 flex items-center justify-center"><Loader2 className="animate-spin text-zinc-500" /></div>;

  return (
    <div className="p-6" data-testid="static-pages-list">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Statične stranice</h1>
          <p className="text-sm text-zinc-500">Impressum, O nama, Politike</p>
        </div>
        <button onClick={runImport} className="flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white text-sm rounded" data-testid="static-import-btn">
          <RefreshCw size={14} /> Re-import sa e-mina.eu
        </button>
      </div>
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-950 text-zinc-400 text-xs">
            <tr>
              <th className="px-4 py-3 text-left">Slug</th>
              <th className="px-4 py-3 text-left">Naslov (HR)</th>
              <th className="px-4 py-3 text-left">Ažurirano</th>
              <th className="px-4 py-3 text-right">Akcije</th>
            </tr>
          </thead>
          <tbody>
            {pages.map(p => (
              <tr key={p.slug} className="border-t border-zinc-800 text-zinc-300">
                <td className="px-4 py-3 font-mono text-xs">{p.slug}</td>
                <td className="px-4 py-3">{p.title_hr}</td>
                <td className="px-4 py-3 text-xs text-zinc-500">{p.updated_at ? new Date(p.updated_at).toLocaleString('hr') : '—'}</td>
                <td className="px-4 py-3 text-right">
                  <Link to={`/admin/static-pages/${p.slug}/edit`} className="inline-flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 mr-4">
                    <FileEdit size={12} /> Uredi
                  </Link>
                  <button onClick={() => retranslate(p.slug)} disabled={retranslating === p.slug} className="inline-flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300 disabled:opacity-50">
                    {retranslating === p.slug ? <Loader2 className="animate-spin" size={12} /> : <RefreshCw size={12} />} Prevedi
                  </button>
                </td>
              </tr>
            ))}
            {pages.length === 0 && <tr><td colSpan={4} className="text-center py-12 text-zinc-500">Nema stranica. Klikni "Re-import" za povlačenje sa e-mina.eu.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
